// The requisition rail's two spine cards: *Req health* from `health.open` ("Req health = numeric score +
// Green/Yellow/Red + transparent drivers."; the Health component, DS row 66: each driver with what it measured and the
// points it moved the score, and only a driver that costs points warns and links) and *Search Readiness* from
// `readiness.open` (the Gate, DS row 67, with its percent ring: each prerequisite a check row in his state words,
// deep-linked to the screen that clears it while open; the open count labelled *Missing prerequisites*, the gate's
// verdict; a pending Compensation review says posting is not blocked by it). Each card renders only for a viewer who
// may call its read; a refusal draws nothing, never a guess. A driver or prerequisite this page has no word for is left
// out rather than shown by its key, and a driver measure with no word of his (the V1 phase states) draws no value.
// A row links only where the reader can go (F-11): a route they can open (a requisition section by its read,
// subRouteReads.ts) that is not the requisition's own page, which they are on; otherwise it is plain text.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getManifest, healthOpen, readinessOpen, type DriverOut, type HealthOpenOutput, type Manifest, type PrerequisiteOut,
  type ReadinessOpenOutput,
} from "@/api/tools";
import {
  Card, COMPENSATION_REVIEW_STATUS, Gate, Health, INTAKE_STATUS, METRIC_LABELS, METRIC_TEMPLATES, RECRUITER_ASSIGNMENT_STATUS,
  REQUEST_STATUS, REQUISITION_STATUS, ROLE_LABELS, SLA_LABELS, SLA_TIER_LABELS, SURFACE_LABELS,
  type GateRow, type HealthBand, type HealthDriver,
} from "@/mc";
import { useSettledRead } from "./data";
import { REQ_LABELS, REQ_RULES } from "./labels";
import { canOpenRoute } from "./subRouteReads";

/** Each health driver's name (B1's driver keys, in his words; D-24: *Days open*, *HM responsiveness*, *Overdue review*
 * lead). `phase_state` is no longer computed; a snapshot stored before D-24 still carries it, and its word stays so that
 * snapshot's score still reads. */
const DRIVER_WORD: Readonly<Record<string, string>> = {
  days_open: METRIC_LABELS.daysOpen,
  hm_responsiveness: METRIC_LABELS.hmResponsiveness,
  overdue_actions: METRIC_LABELS.overdueReview,
  sla_breaches: SLA_TIER_LABELS.breach,
  time_to_acceptance: METRIC_LABELS.timeToAcceptance,
  hold_days: SLA_LABELS.daysOnHold,
  phase_state: REQ_LABELS.phase,
};

const count = (value: unknown): number | undefined => typeof value === "number" && Number.isFinite(value) ? value : undefined;
const days = (value: unknown): string | undefined => {
  const n = count(value);
  return n === undefined ? undefined : METRIC_TEMPLATES.nDays({ n });
};

/** What each driver measured, in words: Days open (Calendar Time), a count, Days on Hold, Time to Acceptance's Active
 * Time against its target. */
const DRIVER_VALUE: Readonly<Record<string, (value: Readonly<Record<string, unknown>>) => string | undefined>> = {
  days_open: (value) => days(value.calendar_days),
  hm_responsiveness: (value) => count(value.count)?.toString(),
  overdue_actions: (value) => count(value.count)?.toString(),
  sla_breaches: (value) => count(value.count)?.toString(),
  time_to_acceptance: (value) => {
    const active = count(value.active_days);
    const target = days(value.target_days);
    if (active === undefined) return undefined;
    return target === undefined ? days(active) : `${active} / ${target}`;
  },
  hold_days: (value) => days(value.days),
};

/** Each Search Readiness prerequisite (B1's keys and the two B2 feeds) in his state words: the record or role it waits
 * on and the state that meets it (his chains, G §5–§6). The posting strategy has no state chain of his; it keeps its
 * record's name. */
const PREREQUISITE_WORD: Readonly<Record<string, readonly string[]>> = {
  request_approved: [REQ_LABELS.hiringRequest, REQUEST_STATUS.approved],
  requisition_created: [REQ_LABELS.requisition, REQUISITION_STATUS.created],
  recruiter_assigned: [ROLE_LABELS.recruiter, RECRUITER_ASSIGNMENT_STATUS.assigned],
  posting_strategy_set: [REQ_LABELS.postingStrategy],
  intake_finalized: [REQ_LABELS.intakePanel, INTAKE_STATUS.finalized],
  compensation_review: [REQ_LABELS.compensationReview, COMPENSATION_REVIEW_STATUS.finalized],
};

/** A server key as a hotspot key part (the key grammar has hyphens, never underscores). */
const hsPart = (key: string): string => key.replace(/_/g, "-");

/** Who the rows link for: the reader's tools, and the requisition whose page draws them, if one does. */
export interface Reach {
  can: (tool: string) => boolean;
  here?: string;
}

/** A route the reader can open that is not the page they are on; with no reader named, the route as it came. */
function linkOf(route: string | null, reach: Reach | undefined): string | undefined {
  if (!route) return undefined;
  if (!reach) return route;
  if (reach.here !== undefined && route.split("?")[0] === `#requisitions/${reach.here}`) return undefined;
  return canOpenRoute(route, reach.can) ? route : undefined;
}

/** The reader's `can`, from the session's manifest read (useSession's own source, by tool name). The cards also draw
 * outside a session (D-7's card tests), where no manifest is loaded and no tool is the reader's. */
function useCan(): (tool: string) => boolean {
  const manifest = useQuery<Manifest>({ queryKey: ["manifest"], queryFn: getManifest, enabled: false }).data;
  return useMemo(() => {
    const tools = new Set((manifest?.tools ?? []).map((t) => t.name));
    return (tool: string) => tools.has(tool);
  }, [manifest]);
}

const BANDS: readonly HealthBand[] = ["green", "yellow", "red"];

export const bandOf = (light: string): HealthBand => (BANDS as readonly string[]).includes(light) ? light as HealthBand : "red";

/** A driver costs points when its weight is below zero; the rest are clear. Every driver carries its measure and
 * its points, so a clear one still says what it read. */
export function driversOf(drivers: readonly DriverOut[], reach?: Reach): HealthDriver[] {
  return [...drivers]
    .sort((a, b) => a.ordinal - b.ordinal)
    .filter((driver) => driver.key in DRIVER_WORD)
    .map((driver) => ({
      status: driver.weight < 0 ? "warn" as const : "ok" as const,
      text: DRIVER_WORD[driver.key] ?? "",
      value: DRIVER_VALUE[driver.key]?.(driver.value ?? {}),
      points: driver.weight,
      href: driver.weight < 0 ? linkOf(driver.route, reach) : undefined,
      hs: `req.health.${hsPart(driver.key)}`,
    }));
}

/** A prerequisite is met (ok), not yet met (todo), or not fed by any lane yet (left out). A Compensation review not
 * yet finalized carries his rule that posting does not wait for it. */
export function prerequisitesOf(prerequisites: readonly PrerequisiteOut[], reach?: Reach): GateRow[] {
  return prerequisites
    .filter((p) => p.fed && p.key in PREREQUISITE_WORD)
    .map((p) => ({
      status: p.met ? "ok" as const : "todo" as const,
      text: (PREREQUISITE_WORD[p.key] ?? []).join(" · "),
      sub: p.key === "compensation_review" && !p.met ? REQ_RULES.postingNotBlocked : undefined,
      href: p.met ? undefined : linkOf(p.route, reach),
      hs: `req.readiness.${hsPart(p.key)}`,
    }));
}

export function HealthCard({ requisitionId, enabled }: { requisitionId: string; enabled: boolean }) {
  const q = useSettledRead<HealthOpenOutput>(["health", requisitionId], () => healthOpen({ requisition_id: requisitionId }), { enabled });
  const can = useCan();
  if (!enabled || q.error) return null;
  return (
    <Card tight title={METRIC_LABELS.reqHealth} >
      <Health
        loading={q.isPending}
        score={q.data?.score ?? 0}
        band={bandOf(q.data?.light ?? "green")}
        drivers={q.data ? driversOf(q.data.drivers, { can, here: requisitionId }) : []}
      />
    </Card>
  );
}

export function ReadinessCard({ requisitionId, enabled }: { requisitionId: string; enabled: boolean }) {
  const q = useSettledRead<ReadinessOpenOutput>(["readiness", requisitionId], () => readinessOpen({ requisition_id: requisitionId }), { enabled });
  const can = useCan();
  if (!enabled || q.error) return null;
  const pct = q.data?.percent;
  const rows = q.data ? prerequisitesOf(q.data.prerequisites, { can, here: requisitionId }) : [];
  return (
    <Gate
      title={METRIC_LABELS.searchReadiness}
      verdict={rows.some((row) => row.status !== "ok") ? SURFACE_LABELS.missingPrerequisites : undefined}
      rows={rows}
      ring={pct == null ? undefined : { pct }}
      loading={q.isPending}
      mode="view"
    />
  );
}
